import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    UsersIcon,
    UserGroupIcon,
    CurrencyDollarIcon,
    TrashIcon,
    FunnelIcon,
    ArrowPathIcon,
    MapPinIcon,
    PhoneIcon,
    AdjustmentsHorizontalIcon,
    Squares2X2Icon,
    ListBulletIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../api/config';
import KPICard from '../components/KPICard';
import ModalLayout from '../components/ModalLayout';
import StatusBadge from '../components/StatusBadge';
import { FarmerForm } from '../components/farmers';
import { formatCurrency } from '../utils/formatters';
import { apiGet, apiPatch, subscribeToDataInvalidation } from '../utils/api';

interface Farmer {
    id: number;
    full_name: string;
    phone: string;
    farmer_code?: string;
    cohort_id: number;
    cohort_name: string;
    household_type: string;
    photo_url?: string;
    status: boolean;
    crops?: string;
    agronomist_id?: number | null;
    agronomist_name?: string | null;
    field_facilitator_id?: number | null;
    field_facilitator_name?: string | null;
    location_coordinates?: string;
    plot_size_hectares?: number;
    mapped_field_count?: number;
    mapped_area_hectares?: number | string;
    vsla_balance?: number;
    last_sale_amount?: number;
    location_address?: string;
    plot_boundary?: string;
    user_id?: number | null;
    mobile_login_email?: string | null;
    mobile_login_active?: boolean | null;
    mobile_login_status?: string | null;
}

interface StaffOption {
    id: number;
    full_name?: string;
    email?: string;
    role?: string;
}

interface FarmField {
    id: number;
    name?: string;
    plot_code?: string;
    crop_type?: string;
    farmer_id?: number;
    farmer_name?: string;
    area_hectares?: number | string;
    assigned_agronomist_id?: number | null;
    assigned_agronomist_name?: string | null;
    assigned_field_facilitator_id?: number | null;
    assigned_field_facilitator_name?: string | null;
    tree_count?: number;
    mapped_tree_count?: number;
    assignment_dirty?: boolean;
}

type ViewMode = 'table' | 'grid';
type TabFilter = 'all' | 'champion' | 'standard' | 'mapped';

const FarmersPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAgronomist = user?.role === 'agronomist';
    const [farmers, setFarmers] = useState<Farmer[]>([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingFarmer, setEditingFarmer] = useState<Farmer | null>(null);
    const [assignmentFarmer, setAssignmentFarmer] = useState<Farmer | null>(null);
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [farmFields, setFarmFields] = useState<FarmField[]>([]);
    const [savingAssignments, setSavingAssignments] = useState(false);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [householdFilter, setHouseholdFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<TabFilter>('all');
    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchFarmers = useCallback(async () => {
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/farmers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setFarmers(data);
            }
        } catch (error) {
            console.error('Failed to fetch farmers', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFarmers();
    }, [fetchFarmers]);

    const fetchAssignmentData = useCallback(async () => {
        try {
            const [staff, plots] = await Promise.all([
                apiGet<any[]>('/api/users').catch(() => []),
                apiGet<any>('/api/maps/farm-plots').catch(() => ({ plots: [] })),
            ]);
            setStaffOptions(Array.isArray(staff) ? staff : []);
            setFarmFields(Array.isArray(plots) ? plots : (plots?.plots || []));
        } catch (error) {
            console.error('Failed to fetch farmer assignment data', error);
        }
    }, []);

    useEffect(() => {
        fetchAssignmentData();
    }, [fetchAssignmentData]);

    useEffect(() => {
        const intervalId = setInterval(fetchFarmers, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [fetchFarmers]);

    useEffect(() => {
        return subscribeToDataInvalidation(() => {
            fetchFarmers();
        });
    }, [fetchFarmers]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await Promise.all([fetchFarmers(), fetchAssignmentData()]);
        setTimeout(() => setIsRefreshing(false), 600);
    };

    const handleCreate = async (data: any) => {
        setFormSubmitting(true);
        setFormError('');
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/farmers`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            const result = await response.json().catch(() => ({}));

            if (response.ok) {
                setIsFormOpen(false);
                await fetchFarmers();
            } else {
                setFormError(result.error || result.message || 'Failed to create farmer. Please try again.');
            }
        } catch (error) {
            console.error('Create failed', error);
            setFormError('Failed to create farmer. Please try again.');
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleUpdate = async (data: any) => {
        if (!editingFarmer) return;
        setFormSubmitting(true);
        setFormError('');
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/farmers/${editingFarmer.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            const result = await response.json().catch(() => ({}));

            if (response.ok) {
                setIsFormOpen(false);
                setEditingFarmer(null);
                await fetchFarmers();
            } else {
                setFormError(result.error || result.message || 'Failed to update farmer. Please try again.');
            }
        } catch (error) {
            console.error('Update failed', error);
            setFormError('Failed to update farmer. Please try again.');
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleDeleteFarmer = async (farmer: Farmer) => {
        const ok = window.confirm(`Delete farmer "${farmer.full_name}"? This cannot be undone.`);
        if (!ok) return;

        setDeletingId(farmer.id);
        try {
            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/farmers/${farmer.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchFarmers();
            } else {
                const err = await response.json().catch(() => ({}));
                alert(err.error || err.message || 'Failed to delete farmer');
            }
        } catch (error) {
            console.error('Delete failed', error);
            alert('Failed to delete farmer');
        } finally {
            setDeletingId(null);
        }
    };

    const filteredFarmers = farmers.filter(farmer => {
        const matchesHousehold = householdFilter === 'all' || farmer.household_type === householdFilter;
        const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? farmer.status : !farmer.status);
        const matchesSearch = farmer.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            farmer.phone.includes(searchQuery) ||
            (farmer.farmer_code || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (farmer.agronomist_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (farmer.location_address || '').toLowerCase().includes(searchQuery.toLowerCase());

        const matchesTab = activeTab === 'all' ||
            (activeTab === 'champion' && farmer.household_type === 'champion') ||
            (activeTab === 'standard' && farmer.household_type !== 'champion') ||
            (activeTab === 'mapped' && (Number(farmer.mapped_field_count || 0) > 0 || farmer.location_coordinates || farmer.plot_boundary));

        return matchesHousehold && matchesStatus && matchesSearch && matchesTab;
    });

    // ── KPI calculations ──
    const totalFarmers = farmers.length;
    const leadFarmers = farmers.filter(f => f.household_type === 'champion').length;
    const mappedFarmers = farmers.filter(f => Number(f.mapped_field_count || 0) > 0 || f.location_coordinates || f.plot_boundary).length;
    const activeFarmers = farmers.filter(f => f.status).length;
    const avgIncome = farmers.length > 0 ?
        Math.round(farmers.reduce((sum, f) => sum + (f.last_sale_amount || 0), 0) / farmers.length) : 0;

    const formatFarmerCategory = (category?: string) => (
        category === 'champion' ? 'Lead Farmer' : 'Standard Farmer'
    );

    const agronomists = staffOptions.filter((s) => s.role === 'agronomist');
    const assignableFields = assignmentFarmer ? farmFields : [];

    const handleFieldAssignmentChange = (fieldId: number, agronomistId: string) => {
        setFarmFields((prev) => prev.map((field) => (
            Number(field.id) === Number(fieldId)
                ? { ...field, assigned_agronomist_id: agronomistId ? Number(agronomistId) : null, assignment_dirty: true }
                : field
        )));
    };

    const handleFarmAssignmentToggle = (fieldId: number, checked: boolean) => {
        if (!assignmentFarmer) return;
        setFarmFields((prev) => prev.map((field) => (
            Number(field.id) === Number(fieldId)
                ? {
                    ...field,
                    assignment_dirty: true,
                    farmer_id: checked ? assignmentFarmer.id : undefined,
                    farmer_name: checked ? assignmentFarmer.full_name : undefined,
                }
                : field
        )));
    };

    const handleSaveAssignments = async () => {
        if (!assignmentFarmer) return;
        try {
            setSavingAssignments(true);
            const changedFields = assignableFields.filter((field) => (
                field.assignment_dirty || Number(field.farmer_id) === Number(assignmentFarmer.id)
            ));
            await Promise.all(changedFields.map((field) => {
                const belongsToCurrentFarmer = Number(field.farmer_id) === Number(assignmentFarmer.id);
                return apiPatch(`/api/maps/farm-plots/${field.id}`, {
                    farmer_id: belongsToCurrentFarmer ? assignmentFarmer.id : null,
                    assigned_agronomist_id: field.assigned_agronomist_id || null,
                });
            }));
            await Promise.all([fetchFarmers(), fetchAssignmentData()]);
            setAssignmentFarmer(null);
        } catch (error: any) {
            alert(error?.message || 'Failed to save assignments');
        } finally {
            setSavingAssignments(false);
        }
    };

    const getAvatarUrl = (farmer: Farmer) => {
        const rawUrl = (farmer as any).photo_url || (farmer as any).profile_url;
        if (!rawUrl) return '/images/default-avatar.svg';
        return rawUrl.startsWith('http') ? rawUrl : `${API_URL}/${String(rawUrl).replace(/^\//, '')}`;
    };

    const tabConfig: { key: TabFilter; label: string; count: number; emoji?: string }[] = [
        { key: 'all', label: 'All Farmers', count: totalFarmers },
        { key: 'champion', label: 'Lead Farmers', count: leadFarmers, emoji: '🏆' },
        { key: 'standard', label: 'Standard', count: totalFarmers - leadFarmers },
        { key: 'mapped', label: 'Mapped', count: mappedFarmers, emoji: '📍' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 p-6">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* ═══════════════════════════════════════════════════
                    HEADER SECTION
                ═══════════════════════════════════════════════════ */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-blue-400 to-brand-blue-600 flex items-center justify-center shadow-lg shadow-brand-blue-600/20">
                            <UsersIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                                Farmers Management
                            </h1>
                            <p className="text-sm text-slate-500 mt-0.5">
                                {totalFarmers} registered · {activeFarmers} active · {mappedFarmers} mapped
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleRefresh}
                            className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-500 hover:text-brand-blue-600 hover:border-brand-blue-200 transition-all shadow-sm"
                            title="Refresh data"
                        >
                            <ArrowPathIcon className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={() => { setEditingFarmer(null); setIsFormOpen(true); }}
                            className="bg-brand-blue-600 hover:bg-brand-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-brand-blue-600/20 hover:shadow-brand-blue-600/30"
                        >
                            <PlusIcon className="w-5 h-5" />
                            Add Farmer
                        </button>
                    </div>
                </div>

                {/* ═══════════════════════════════════════════════════
                    KPI CARDS
                ═══════════════════════════════════════════════════ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <KPICard
                        title="Total Farmers"
                        value={totalFarmers}
                        icon={<UsersIcon />}
                        color="blue"
                        subtitle={`${activeFarmers} active`}
                    />
                    <KPICard
                        title="Lead Farmers"
                        value={leadFarmers}
                        icon={<UserGroupIcon />}
                        color="purple"
                        subtitle={`${totalFarmers > 0 ? Math.round((leadFarmers / totalFarmers) * 100) : 0}% of total`}
                    />
                    <KPICard
                        title="Mapped Farms"
                        value={mappedFarmers}
                        icon={<MapPinIcon />}
                        color="emerald"
                        subtitle={`${totalFarmers > 0 ? Math.round((mappedFarmers / totalFarmers) * 100) : 0}% coverage`}
                    />
                    <KPICard
                        title="Avg Income"
                        value={formatCurrency(avgIncome)}
                        icon={<CurrencyDollarIcon />}
                        color="amber"
                        subtitle="per farmer"
                    />
                </div>

                {/* ═══════════════════════════════════════════════════
                    MAIN CONTENT CARD
                ═══════════════════════════════════════════════════ */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">

                    {/* Tab Bar */}
                    <div className="flex items-center gap-1 px-6 pt-4 border-b border-slate-100 overflow-x-auto">
                        {tabConfig.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex items-center gap-2 pb-3 px-4 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
                                    activeTab === tab.key
                                        ? 'border-brand-blue-600 text-brand-blue-600'
                                        : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'
                                }`}
                            >
                                {tab.emoji && <span className="text-base">{tab.emoji}</span>}
                                {tab.label}
                                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                                    activeTab === tab.key
                                        ? 'bg-brand-blue-100 text-brand-blue-700'
                                        : 'bg-slate-100 text-slate-500'
                                }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Toolbar */}
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
                                <div className="relative flex-1 max-w-md">
                                    <MagnifyingGlassIcon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name, phone, code, agronomist, or address..."
                                        className="pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-blue-500/20 focus:border-brand-blue-400 w-full text-sm bg-white transition-all"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
                                    <button
                                        onClick={() => setViewMode('table')}
                                        className={`p-1.5 rounded-lg transition-all ${viewMode === 'table' ? 'bg-brand-blue-50 text-brand-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                                        title="Table view"
                                    >
                                        <ListBulletIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-brand-blue-50 text-brand-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                                        title="Grid view"
                                    >
                                        <Squares2X2Icon className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="h-6 w-px bg-slate-200 mx-1" />

                                <select
                                    className="px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-brand-blue-500/20 focus:border-brand-blue-400 transition-all"
                                    value={householdFilter}
                                    onChange={e => setHouseholdFilter(e.target.value)}
                                >
                                    <option value="all">All Types</option>
                                    <option value="standard">Standard</option>
                                    <option value="champion">Lead Farmer</option>
                                </select>
                                <select
                                    className="px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-brand-blue-500/20 focus:border-brand-blue-400 transition-all"
                                    value={statusFilter}
                                    onChange={e => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="h-[calc(100vh-22rem)] overflow-auto">
                        {loading ? (
                            /* ── Loading Skeleton ── */
                            <div className="p-6 space-y-4">
                                {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex items-center gap-4 p-4 rounded-xl animate-pulse">
                                        <div className="h-12 w-12 bg-slate-200 rounded-full" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-slate-200 rounded w-1/4" />
                                            <div className="h-3 bg-slate-100 rounded w-1/3" />
                                        </div>
                                        <div className="h-6 w-16 bg-slate-200 rounded-full" />
                                        <div className="h-6 w-16 bg-slate-200 rounded-full" />
                                        <div className="h-8 w-20 bg-slate-200 rounded-lg" />
                                    </div>
                                ))}
                            </div>
                        ) : filteredFarmers.length === 0 ? (
                            /* ── Empty State ── */
                            <div className="flex flex-col items-center justify-center h-full py-16 px-8">
                                <div className="w-20 h-20 rounded-3xl bg-slate-100 flex items-center justify-center mb-6">
                                    <UsersIcon className="w-10 h-10 text-slate-300" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-700 mb-2">
                                    {isAgronomist ? 'No farmers assigned to you yet' : 'No farmers found'}
                                </h3>
                                <p className="text-sm text-slate-400 text-center max-w-sm mb-6">
                                    {isAgronomist
                                        ? 'Ask your Project Manager to assign farms or farmers to you.'
                                        : searchQuery || householdFilter !== 'all' || statusFilter !== 'all' || activeTab !== 'all'
                                            ? 'Try adjusting your search or filters to find what you\'re looking for.'
                                            : 'Get started by adding your first farmer to the system.'}
                                </p>
                                {!isAgronomist && !searchQuery && activeTab === 'all' && (
                                    <button
                                        onClick={() => { setEditingFarmer(null); setIsFormOpen(true); }}
                                        className="bg-brand-blue-600 hover:bg-brand-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-brand-blue-600/20"
                                    >
                                        <PlusIcon className="w-5 h-5" />
                                        Add First Farmer
                                    </button>
                                )}
                            </div>
                        ) : viewMode === 'table' ? (
                            /* ── Table View ── */
                            <table className="min-w-full">
                                <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm">
                                    <tr>
                                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Farmer</th>
                                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Code</th>
                                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Mapped Area</th>
                                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Agronomist</th>
                                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</th>
                                        <th className="px-6 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredFarmers.map((farmer) => (
                                        <tr
                                            key={farmer.id}
                                            className="hover:bg-brand-blue-50/40 transition-all duration-150 cursor-pointer group"
                                            onClick={() => navigate(`/dashboard/farmers/${farmer.id}`)}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="relative">
                                                        <img
                                                            className="h-11 w-11 rounded-xl object-cover border-2 border-slate-200 group-hover:border-brand-blue-300 transition-all duration-200 shadow-sm"
                                                            src={getAvatarUrl(farmer)}
                                                            alt=""
                                                            onError={(e) => {
                                                                e.currentTarget.src = '/images/default-avatar.svg';
                                                            }}
                                                        />
                                                        {farmer.household_type === 'champion' && (
                                                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center text-[10px] shadow-sm border border-white">
                                                                🏆
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-slate-900 group-hover:text-brand-blue-700 transition-colors">
                                                            {farmer.full_name}
                                                        </div>
                                                        <div className="text-xs text-slate-400 capitalize">
                                                            {formatFarmerCategory(farmer.household_type)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 font-mono text-xs font-bold text-slate-600">
                                                    {farmer.farmer_code || '—'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-[180px]">
                                                    <div className="flex items-center gap-1.5 text-sm text-slate-600 truncate" title={farmer.location_address || ''}>
                                                        <MapPinIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                        {farmer.location_address ? (
                                                            <span className="truncate">{farmer.location_address}</span>
                                                        ) : (
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setEditingFarmer(farmer); setIsFormOpen(true); }}
                                                                className="text-xs text-amber-600 hover:text-amber-700 font-semibold underline decoration-dotted underline-offset-2"
                                                                title="Set address"
                                                            >
                                                                Not set — add
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {Number(farmer.mapped_area_hectares || 0) > 0 ? (
                                                    <div>
                                                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            {Number(farmer.mapped_area_hectares).toFixed(2)} ha
                                                        </span>
                                                        {Number(farmer.mapped_field_count || 0) > 0 && (
                                                            <div className="mt-1 text-xs text-slate-400">
                                                                {farmer.mapped_field_count} field{Number(farmer.mapped_field_count) === 1 ? '' : 's'}
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : farmer.plot_size_hectares ? (
                                                    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 text-slate-600">
                                                        {farmer.plot_size_hectares} ha
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/maps?create=1&farmer=${farmer.id}`); }}
                                                        className="text-xs text-amber-600 hover:text-amber-700 font-semibold underline decoration-dotted underline-offset-2"
                                                        title="Map this farmer's field"
                                                    >
                                                        Not mapped — map
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-slate-700">
                                                    {farmer.agronomist_name || (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setAssignmentFarmer(farmer); }}
                                                            className="text-xs text-amber-600 hover:text-amber-700 font-semibold underline decoration-dotted underline-offset-2"
                                                            title="Assign agronomist"
                                                        >
                                                            Not assigned — assign
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                                    <PhoneIcon className="w-3.5 h-3.5 text-slate-400" />
                                                    {farmer.phone || '—'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <StatusBadge status={farmer.status ? 'Active' : 'Inactive'} size="sm" />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setAssignmentFarmer(farmer); }}
                                                        className="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50 transition-all"
                                                        title="Assign farm fields"
                                                    >
                                                        Assign Farm
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setEditingFarmer(farmer); setIsFormOpen(true); }}
                                                        className="p-2 rounded-lg text-slate-400 hover:text-brand-blue-600 hover:bg-brand-blue-50 transition-all"
                                                        title="Edit farmer"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteFarmer(farmer); }}
                                                        disabled={deletingId === farmer.id}
                                                        className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                                                        title="Delete farmer"
                                                    >
                                                        {deletingId === farmer.id
                                                            ? <span className="block h-4 w-4 animate-spin rounded-full border-2 border-red-300 border-t-red-600" />
                                                            : <TrashIcon className="w-4 h-4" />}
                                                    </button>
                                                    <ChevronRightIcon className="w-4 h-4 text-slate-300 ml-1" />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            /* ── Grid View ── */
                            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {filteredFarmers.map((farmer) => (
                                    <div
                                        key={farmer.id}
                                        onClick={() => navigate(`/dashboard/farmers/${farmer.id}`)}
                                        className="group relative bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-lg hover:border-brand-blue-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                                    >
                                        {/* Champion badge */}
                                        {farmer.household_type === 'champion' && (
                                            <div className="absolute top-3 right-3">
                                                <span className="text-lg">🏆</span>
                                            </div>
                                        )}

                                        {/* Avatar */}
                                        <div className="flex items-center gap-3 mb-4">
                                            <img
                                                className="h-12 w-12 rounded-xl object-cover border-2 border-slate-200 group-hover:border-brand-blue-300 transition-all shadow-sm"
                                                src={getAvatarUrl(farmer)}
                                                alt=""
                                                onError={(e) => { e.currentTarget.src = '/images/default-avatar.svg'; }}
                                            />
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-brand-blue-700 transition-colors">
                                                    {farmer.full_name}
                                                </div>
                                                <div className="text-xs text-slate-400 font-mono">
                                                    {farmer.farmer_code || 'No code'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Info */}
                                        <div className="space-y-2 mb-4">
                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                <PhoneIcon className="w-3.5 h-3.5 text-slate-400" />
                                                {farmer.phone || <span className="text-amber-500 italic">No phone</span>}
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-slate-500 truncate">
                                                <MapPinIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                {farmer.location_address ? (
                                                    <span className="truncate">{farmer.location_address}</span>
                                                ) : (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setEditingFarmer(farmer); setIsFormOpen(true); }}
                                                        className="text-amber-600 hover:text-amber-700 font-semibold underline decoration-dotted underline-offset-2"
                                                    >
                                                        No address — add
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Footer badges */}
                                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                            <StatusBadge status={farmer.status ? 'Active' : 'Inactive'} size="sm" />
                                            {Number(farmer.mapped_area_hectares || 0) > 0 ? (
                                                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                                                    {Number(farmer.mapped_area_hectares).toFixed(1)} ha
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/maps?create=1&farmer=${farmer.id}`); }}
                                                    className="text-xs text-amber-600 hover:text-amber-700 font-semibold underline decoration-dotted underline-offset-2"
                                                >
                                                    No map — map
                                                </button>
                                            )}
                                        </div>

                                        {/* Quick actions */}
                                        <div className="absolute bottom-5 right-5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setEditingFarmer(farmer); setIsFormOpen(true); }}
                                                className="p-1.5 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-brand-blue-600 hover:border-brand-blue-200 shadow-sm transition-all"
                                                title="Edit"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setAssignmentFarmer(farmer); }}
                                                className="p-1.5 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-emerald-600 hover:border-emerald-200 shadow-sm transition-all"
                                                title="Assign"
                                            >
                                                <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-slate-50/80 border-t border-slate-100 px-6 py-3 flex justify-between items-center text-sm text-slate-500 backdrop-blur-sm">
                        <span>
                            Showing <span className="font-semibold text-slate-700">{filteredFarmers.length}</span> of{' '}
                            <span className="font-semibold text-slate-700">{totalFarmers}</span> farmers
                        </span>
                        <span className="text-slate-400">Click any row to view 360° profile</span>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════
                FARMER FORM MODAL
            ═══════════════════════════════════════════════════ */}
            {isFormOpen && (
                <ModalLayout
                    isOpen={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                    title={editingFarmer ? "Edit Farmer" : "Add New Farmer"}
                    size="2xl"
                    footer={null}
                >
                    <FarmerForm
                        initialData={editingFarmer}
                        onSubmit={editingFarmer ? handleUpdate : handleCreate}
                        onCancel={() => setIsFormOpen(false)}
                        isLoading={formSubmitting}
                        errorMessage={formError}
                    />
                </ModalLayout>
            )}

            {/* ═══════════════════════════════════════════════════
                ASSIGNMENT MODAL
            ═══════════════════════════════════════════════════ */}
            {assignmentFarmer && (
                <ModalLayout
                    isOpen={Boolean(assignmentFarmer)}
                    onClose={() => setAssignmentFarmer(null)}
                    title={`Assign Farm Fields`}
                    size="2xl"
                    footer={null}
                >
                    <div className="space-y-5">
                        {/* Farmer info banner */}
                        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-brand-blue-50 to-emerald-50 rounded-2xl border border-brand-blue-100">
                            <img
                                className="h-12 w-12 rounded-xl object-cover border-2 border-white shadow-sm"
                                src={getAvatarUrl(assignmentFarmer)}
                                alt=""
                                onError={(e) => { e.currentTarget.src = '/images/default-avatar.svg'; }}
                            />
                            <div>
                                <p className="font-bold text-slate-900">{assignmentFarmer.full_name}</p>
                                <p className="text-sm text-slate-500">
                                    {assignmentFarmer.farmer_code && <span className="font-mono">{assignmentFarmer.farmer_code}</span>}
                                    {assignmentFarmer.farmer_code && ' · '}
                                    {assignmentFarmer.phone}
                                </p>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                            <p className="text-sm font-semibold text-emerald-800">Farm & Agronomist Assignment</p>
                            <p className="mt-1 text-sm leading-relaxed text-emerald-700">
                                Assign mapped farm fields and choose the agronomist responsible for follow-up. These assignments drive dashboard visibility, mobile work lists, and traceability.
                            </p>
                        </div>

                        {assignableFields.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                                    <MapPinIcon className="w-8 h-8 text-slate-300" />
                                </div>
                                <p className="text-base font-bold text-slate-700">No farm fields available</p>
                                <p className="mt-2 text-sm text-slate-400 max-w-sm mx-auto">
                                    Create a farm boundary in Farm Maps first. Once a field exists, you can assign it here.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Assign</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Field</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Current Farmer</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Crop</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Area</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Trees</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-500 text-xs uppercase tracking-wider">Agronomist</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {assignableFields.map((field) => {
                                            const isAssignedToFarmer = Number(field.farmer_id) === Number(assignmentFarmer.id);
                                            return (
                                                <tr key={field.id} className={`transition-colors ${isAssignedToFarmer ? 'bg-emerald-50/50' : 'hover:bg-slate-50'}`}>
                                                    <td className="px-4 py-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={isAssignedToFarmer}
                                                            onChange={(event) => handleFarmAssignmentToggle(field.id, event.target.checked)}
                                                            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                                            aria-label={`Assign ${field.name || field.plot_code || `Field ${field.id}`}`}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p className="font-semibold text-slate-900">{field.name || field.plot_code || `Field ${field.id}`}</p>
                                                        <p className="text-xs font-mono text-slate-400">{field.plot_code || 'No code'}</p>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <p className={`text-sm font-medium ${isAssignedToFarmer ? 'text-emerald-700' : 'text-slate-600'}`}>
                                                            {field.farmer_name || 'Unassigned'}
                                                        </p>
                                                        {!isAssignedToFarmer && field.farmer_name && (
                                                            <p className="mt-0.5 text-xs text-amber-600">→ will move to {assignmentFarmer.full_name}</p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 capitalize text-slate-600 text-sm">{field.crop_type || 'Not set'}</td>
                                                    <td className="px-4 py-3 text-slate-600 text-sm font-medium">{Number(field.area_hectares || 0).toFixed(2)} ha</td>
                                                    <td className="px-4 py-3 text-slate-600 text-sm">{field.mapped_tree_count || 0}/{field.tree_count || 0}</td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            value={field.assigned_agronomist_id || ''}
                                                            onChange={(event) => handleFieldAssignmentChange(field.id, event.target.value)}
                                                            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                                                        >
                                                            <option value="">Not assigned</option>
                                                            {agronomists.map((s) => (
                                                                <option key={s.id} value={s.id}>{s.full_name || s.email || `User ${s.id}`}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setAssignmentFarmer(null)}
                                className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveAssignments}
                                disabled={savingAssignments || assignableFields.length === 0}
                                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-200 hover:bg-emerald-700 disabled:opacity-60 transition-all"
                            >
                                {savingAssignments ? (
                                    <span className="flex items-center gap-2">
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                        Saving...
                                    </span>
                                ) : 'Save Assignments'}
                            </button>
                        </div>
                    </div>
                </ModalLayout>
            )}

        </div>
    );
};

export default FarmersPage;
